export const TASTE_AXES = Object.freeze([
  Object.freeze({ key: "woody", label: "Woody", tone: "#d39a63" }),
  Object.freeze({ key: "winey", label: "Winey", tone: "#d97672" }),
  Object.freeze({ key: "cereal", label: "Cereal", tone: "#d6bb62" }),
  Object.freeze({ key: "fruity", label: "Fruity", tone: "#e58764" }),
  Object.freeze({ key: "floral", label: "Floral", tone: "#c58bd1" }),
  Object.freeze({ key: "peaty", label: "Peaty", tone: "#72b5a3" }),
  Object.freeze({ key: "feinty", label: "Feinty", tone: "#779fc2" }),
  Object.freeze({ key: "sulphury", label: "Sulphury", tone: "#c9cf9b" }),
]);

const ACTIVE_FAMILY_SCORE_THRESHOLD = 0.01;
const MAX_ABSOLUTE_RMS_DISTANCE = 1;

export const DEFAULT_METADATA_WEIGHT = 0.25;

const METADATA_SIGNAL_WEIGHTS = Object.freeze({
  cask: 0.22,
  productionStyle: 0.16,
  whiskyFamily: 0.14,
  age: 0.11,
  abv: 0.09,
  country: 0.07,
  region: 0.05,
  stillTerms: 0.04,
  strengthLabel: 0.02,
  peatedState: 0.10,
});

const CASK_COMPONENT_WEIGHTS = Object.freeze({
  seasoning: 0.40,
  wood: 0.20,
  treatment: 0.15,
  maturation_style: 0.10,
  vessel: 0.10,
  special: 0.05,
  fallback: 1,
});

const clamp01 = (value) => Math.max(0, Math.min(1, Number(value) || 0));

function normalizeMetadataToken(value) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function cleanMetadataText(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function splitMetadataValues(value) {
  if (Array.isArray(value)) return value.flatMap((item) => splitMetadataValues(item));
  if (value && typeof value === "object") {
    return splitMetadataValues(value.normalized_value ?? value.value ?? value.label ?? "");
  }
  return String(value ?? "")
    .split(/\s*(?:,|;|\+|\/|&|\band\b)\s*/i)
    .map(cleanMetadataText)
    .filter(Boolean);
}

function toTokenMap(value) {
  const out = new Map();
  for (const label of splitMetadataValues(value)) {
    const token = normalizeMetadataToken(label);
    if (token && !out.has(token)) out.set(token, label);
  }
  return out;
}

function addCaskFamilyTokens(tokens, label) {
  const normalized = normalizeMetadataToken(label);
  const families = [];
  if (/\b(?:oloroso|pedro ximenez|px|amontillado|fino|manzanilla|palo cortado|sherry)\b/.test(normalized)) families.push("sherry");
  if (/\b(?:port|tawny|ruby)\b/.test(normalized)) families.push("port");
  if (/\b(?:bourbon|american whiskey)\b/.test(normalized)) families.push("bourbon");
  if (/\b(?:wine|cabernet|merlot|pinot|sauternes|madeira|marsala)\b/.test(normalized)) families.push("wine");
  if (/\b(?:american oak|european oak|french oak|oak|quercus)\b/.test(normalized)) families.push("oak");
  for (const family of families) tokens.set(`family:${family}`, family);
}

function readCaskComponents(meta) {
  const grouped = new Map();
  const components = Array.isArray(meta?.cask_components) ? meta.cask_components : [];
  for (const component of components) {
    const type = normalizeMetadataToken(component?.component_type).replace(/\s+/g, "_");
    const label = cleanMetadataText(component?.normalized_value);
    const token = normalizeMetadataToken(label);
    if (!type || !token) continue;
    if (!grouped.has(type)) grouped.set(type, new Map());
    const values = grouped.get(type);
    values.set(token, label);
    addCaskFamilyTokens(values, label);
  }
  if (!grouped.size) {
    const fallback = toTokenMap(meta?.cask);
    for (const label of fallback.values()) addCaskFamilyTokens(fallback, label);
    if (fallback.size) grouped.set("fallback", fallback);
  }
  return grouped;
}

function jaccardScore(leftMap, rightMap) {
  if (!leftMap?.size || !rightMap?.size) return null;
  const union = new Set([...leftMap.keys(), ...rightMap.keys()]);
  let intersection = 0;
  for (const token of leftMap.keys()) {
    if (rightMap.has(token)) intersection += 1;
  }
  return union.size ? intersection / union.size : null;
}

function scoreCaskMetadata(leftMeta, rightMeta) {
  const left = readCaskComponents(leftMeta);
  const right = readCaskComponents(rightMeta);
  const parts = [];
  const sharedLabels = [];
  for (const [type, leftValues] of left.entries()) {
    const rightValues = right.get(type);
    if (!rightValues?.size) continue;
    const score = jaccardScore(leftValues, rightValues);
    if (!Number.isFinite(score)) continue;
    const weight = CASK_COMPONENT_WEIGHTS[type] ?? 0.05;
    parts.push({ score, weight });
    for (const [token, label] of leftValues.entries()) {
      if (rightValues.has(token) && !token.startsWith("family:") && !sharedLabels.includes(label)) {
        sharedLabels.push(label);
      }
    }
    if (!sharedLabels.length) {
      for (const [token, label] of leftValues.entries()) {
        if (rightValues.has(token) && token.startsWith("family:") && !sharedLabels.includes(label)) {
          sharedLabels.push(label);
        }
      }
    }
  }
  if (!parts.length) return { score: null, reason: "" };
  const weightTotal = parts.reduce((total, part) => total + part.weight, 0);
  const score = parts.reduce((total, part) => total + (part.score * part.weight), 0) / weightTotal;
  const label = sharedLabels[0];
  return {
    score,
    reason: label && score > 0 ? `Shared ${label} maturation` : "",
  };
}

function scoreExactMetadata(leftValue, rightValue, reason = "", normalizer = normalizeMetadataToken) {
  const left = normalizer(leftValue);
  const right = normalizer(rightValue);
  if (!left || !right) return { score: null, reason: "" };
  const matches = left === right;
  return { score: matches ? 1 : 0, reason: matches ? reason : "" };
}

function scoreProductionStyle(leftValue, rightValue) {
  const left = normalizeMetadataToken(leftValue);
  const right = normalizeMetadataToken(rightValue);
  if (!left || !right) return { score: null, reason: "" };
  if (left === right) return { score: 1, reason: `Both ${cleanMetadataText(rightValue)}` };
  const sameBase = ["malt", "grain"].some((term) => left.includes(term) && right.includes(term));
  if (sameBase) return { score: 0.65, reason: "Related production style" };
  const sameConstruction = ["single", "blended"].some((term) => left.includes(term) && right.includes(term));
  return { score: sameConstruction ? 0.25 : 0, reason: sameConstruction ? "Related production style" : "" };
}

function familyParent(value) {
  const token = normalizeMetadataToken(value);
  if (/\b(?:bourbon|rye|tennessee|american whiskey)\b/.test(token)) return "american whiskey";
  return token;
}

function scoreWhiskyFamily(leftValue, rightValue) {
  const left = normalizeMetadataToken(leftValue);
  const right = normalizeMetadataToken(rightValue);
  if (!left || !right) return { score: null, reason: "" };
  if (left === right) return { score: 1, reason: `Same ${cleanMetadataText(rightValue)} family` };
  if (familyParent(left) === familyParent(right)) {
    const bourbonTennessee = /bourbon/.test(left + right) && /tennessee/.test(left + right);
    return { score: bourbonTennessee ? 0.7 : 0.6, reason: "Related whisky family" };
  }
  return { score: 0, reason: "" };
}

function readFiniteNumber(value, { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY } = {}) {
  const match = String(value ?? "").replace(",", ".").match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) && number >= min && number <= max ? number : null;
}

function scoreNumericMetadata(leftValue, rightValue, range, bounds, reasonBuilder) {
  const left = readFiniteNumber(leftValue, bounds);
  const right = readFiniteNumber(rightValue, bounds);
  if (left == null || right == null) return { score: null, reason: "" };
  const difference = Math.abs(left - right);
  return {
    score: clamp01(1 - (difference / range)),
    reason: difference <= (range * 0.2) ? reasonBuilder(left, right, difference) : "",
  };
}

function canonicalRegion(value) {
  return normalizeMetadataToken(value).replace(/\b(lowland|highland|island)s\b/g, "$1");
}

function scoreSetMetadata(leftValue, rightValue, reasonLabel) {
  const left = toTokenMap(leftValue);
  const right = toTokenMap(rightValue);
  if (!left.size || !right.size) return { score: null, reason: "" };
  if (left.has("single pot still")) left.set("pot still", "Pot Still");
  if (right.has("single pot still")) right.set("pot still", "Pot Still");
  const score = jaccardScore(left, right);
  const shared = Array.from(left.entries()).find(([token]) => right.has(token));
  return {
    score,
    reason: shared && score > 0 ? `${reasonLabel} ${shared[1]}` : "",
  };
}

function scoreStrengthLabel(leftValue, rightValue) {
  const exact = scoreExactMetadata(leftValue, rightValue, `Both ${cleanMetadataText(rightValue)}`);
  if (exact.score == null || exact.score === 1) return exact;
  const left = normalizeMetadataToken(leftValue);
  const right = normalizeMetadataToken(rightValue);
  const highStrength = (value) => /cask strength|barrel proof|high proof|navy strength/.test(value);
  return highStrength(left) && highStrength(right)
    ? { score: 0.6, reason: "Similar high-strength bottling" }
    : exact;
}

function peatedStateLevel(value) {
  const token = normalizeMetadataToken(value);
  if (!token) return null;
  if (token.includes("unpeated")) return 0;
  if (token.includes("light") && token.includes("peat")) return 0.35;
  if (token.includes("heav") && token.includes("peat")) return 1;
  if (token.includes("peat")) return 0.65;
  return null;
}

function scorePeatedState(leftValue, rightValue) {
  const left = peatedStateLevel(leftValue);
  const right = peatedStateLevel(rightValue);
  if (left == null || right == null) return { score: null, reason: "" };
  const score = clamp01(1 - Math.abs(left - right));
  return {
    score,
    reason: score >= 0.7 ? "Similar peat level" : "",
  };
}

function scoreMetadata(leftMeta, rightMeta) {
  const comparisons = {
    cask: scoreCaskMetadata(leftMeta, rightMeta),
    productionStyle: scoreProductionStyle(leftMeta?.production_style, rightMeta?.production_style),
    whiskyFamily: scoreWhiskyFamily(leftMeta?.whisky_family, rightMeta?.whisky_family),
    age: scoreNumericMetadata(
      leftMeta?.age,
      rightMeta?.age,
      20,
      { min: 1, max: 100 },
      (left, right, difference) => difference === 0 ? `Both ${left} years old` : `Similar age (${left}/${right} years)`,
    ),
    abv: scoreNumericMetadata(
      leftMeta?.abv,
      rightMeta?.abv,
      15,
      { min: 20, max: 95 },
      (left, right) => `Similar strength (${left}/${right}% ABV)`,
    ),
    country: scoreExactMetadata(
      leftMeta?.country,
      rightMeta?.country,
      rightMeta?.country ? `Both from ${cleanMetadataText(rightMeta.country)}` : "",
    ),
    region: scoreExactMetadata(
      leftMeta?.region,
      rightMeta?.region,
      rightMeta?.region ? `Both from ${cleanMetadataText(rightMeta.region)}` : "",
      canonicalRegion,
    ),
    stillTerms: scoreSetMetadata(leftMeta?.still_terms, rightMeta?.still_terms, "Shared production method:"),
    strengthLabel: scoreStrengthLabel(leftMeta?.strength_label, rightMeta?.strength_label),
    peatedState: scorePeatedState(leftMeta?.peated_state, rightMeta?.peated_state),
  };

  let coverage = 0;
  let weightedObservedScore = 0;
  const reasons = [];
  for (const [key, comparison] of Object.entries(comparisons)) {
    const weight = METADATA_SIGNAL_WEIGHTS[key];
    if (!Number.isFinite(comparison?.score)) continue;
    coverage += weight;
    weightedObservedScore += comparison.score * weight;
    if (comparison.reason && comparison.score >= 0.55) {
      reasons.push({ group: key, label: comparison.reason, contribution: comparison.score * weight });
    }
  }
  reasons.sort((left, right) => (right.contribution - left.contribution) || left.label.localeCompare(right.label));

  return {
    similarity: clamp01(weightedObservedScore + ((1 - coverage) * 0.5)),
    coverage: clamp01(coverage),
    comparisons,
    reasons,
  };
}

function getSharedTasteReason(leftProfile, rightProfile) {
  const left = leftProfile.categories;
  const right = rightProfile.categories;
  const shared = TASTE_AXES
    .map((axis) => ({ label: axis.label.toLowerCase(), value: Math.min(left[axis.key], right[axis.key]) }))
    .filter((axis) => axis.value >= 0.05)
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .map((axis) => axis.label);
  if (!shared.length) return "Related flavour character";
  return `Shared ${shared.join(" + ")} character`;
}

function buildNoveltyReasons(leftMeta, rightMeta) {
  const reasons = [];
  const leftBrand = normalizeMetadataToken(leftMeta?.brand);
  const rightBrand = normalizeMetadataToken(rightMeta?.brand);
  if (leftBrand && rightBrand && leftBrand !== rightBrand) {
    reasons.push({ group: "brand", label: `New producer: ${cleanMetadataText(rightMeta.brand)}` });
  }
  const leftCountry = normalizeMetadataToken(leftMeta?.country);
  const rightCountry = normalizeMetadataToken(rightMeta?.country);
  if (leftCountry && rightCountry && leftCountry !== rightCountry) {
    reasons.push({ group: "country", label: `Explore ${cleanMetadataText(rightMeta.country)}` });
  }
  const leftFamily = normalizeMetadataToken(leftMeta?.whisky_family);
  const rightFamily = normalizeMetadataToken(rightMeta?.whisky_family);
  if (leftFamily && rightFamily && leftFamily !== rightFamily) {
    reasons.push({ group: "family", label: `Different family: ${cleanMetadataText(rightMeta.whisky_family)}` });
  }
  return reasons.slice(0, 2);
}

export function normalizeTasteVector(rawVector) {
  const categories = rawVector?.categories;
  if (!categories || typeof categories !== "object") return null;

  const normalizedCategories = {};
  let magnitudeSquared = 0;
  for (const axis of TASTE_AXES) {
    const value = Number(categories[axis.key]);
    if (!Number.isFinite(value) || value < 0 || value > 1) return null;
    normalizedCategories[axis.key] = value;
    magnitudeSquared += value * value;
  }

  const includedCount = Number(rawVector?.included_count);
  const availableFieldCount = Number(rawVector?.available_field_count);
  if (!Number.isFinite(includedCount) || includedCount <= 0) return null;
  if (!Number.isFinite(availableFieldCount) || availableFieldCount <= 0) return null;
  if (magnitudeSquared <= Number.EPSILON) return null;

  const categoryTotal = Object.values(normalizedCategories).reduce((total, value) => total + value, 0);
  const compositionCategories = Object.fromEntries(
    TASTE_AXES.map((axis) => [axis.key, normalizedCategories[axis.key] / categoryTotal]),
  );

  return {
    ...rawVector,
    categories: normalizedCategories,
    compositionCategories,
    categoryTotal,
    activeFamilyCount: TASTE_AXES.filter(
      (axis) => normalizedCategories[axis.key] >= ACTIVE_FAMILY_SCORE_THRESHOLD,
    ).length,
  };
}

function absoluteTasteDistance(left, right) {
  let distanceSquared = 0;
  const leftCategories = left.categories;
  const rightCategories = right.categories;

  for (const axis of TASTE_AXES) {
    const leftValue = leftCategories[axis.key];
    const rightValue = rightCategories[axis.key];
    const difference = leftValue - rightValue;
    distanceSquared += difference * difference;
  }
  return Math.sqrt(distanceSquared / TASTE_AXES.length);
}

export function tasteProfileDistance(leftVector, rightVector) {
  const left = normalizeTasteVector(leftVector);
  const right = normalizeTasteVector(rightVector);
  if (!left || !right) return null;
  return absoluteTasteDistance(left, right);
}

export function scoreTasteMatch(
  sourceVector,
  candidateVector,
  { sourceMeta = null, candidateMeta = null, metadataWeight = DEFAULT_METADATA_WEIGHT } = {},
) {
  const source = normalizeTasteVector(sourceVector);
  const candidate = normalizeTasteVector(candidateVector);
  if (!source || !candidate) return null;

  const distance = absoluteTasteDistance(source, candidate);
  const tasteSimilarity = clamp01(1 - (distance / MAX_ABSOLUTE_RMS_DISTANCE));
  const hasMetadataContext = !!sourceMeta && typeof sourceMeta === "object";
  const effectiveMetadataWeight = hasMetadataContext ? Math.max(0, Math.min(0.5, Number(metadataWeight) || 0)) : 0;
  const metadata = hasMetadataContext
    ? scoreMetadata(sourceMeta, candidateMeta || {})
    : { similarity: 0.5, coverage: 0, comparisons: {}, reasons: [] };
  const similarityScore = (tasteSimilarity * (1 - effectiveMetadataWeight))
    + (metadata.similarity * effectiveMetadataWeight);

  return {
    source,
    candidate,
    distance,
    tasteSimilarity,
    metadataSimilarity: metadata.similarity,
    metadataCoverage: metadata.coverage,
    similarityScore: clamp01(similarityScore),
    metadataComparisons: metadata.comparisons,
    reasons: [
      { group: "taste", label: getSharedTasteReason(source, candidate), contribution: tasteSimilarity },
      ...metadata.reasons,
    ],
    noveltyReasons: hasMetadataContext ? buildNoveltyReasons(sourceMeta, candidateMeta || {}) : [],
  };
}

function vectorVersionsAreCompatible(left, right) {
  for (const key of ["vector_version", "lexicon_version"]) {
    const leftVersion = String(left?.[key] || "").trim();
    const rightVersion = String(right?.[key] || "").trim();
    if (leftVersion !== rightVersion) return false;
  }
  return true;
}

function tasteEvidenceScore(vector) {
  const includedCount = Number(vector?.included_count);
  const scoredRate = Number(vector?.scored_rate);
  const meanConfidence = Number(vector?.mean_confidence);
  const includedScore = Number.isFinite(includedCount) ? Math.min(1, includedCount / 12) : 0;
  const coverageScore = Number.isFinite(scoredRate) ? Math.max(0, Math.min(1, scoredRate)) : 0;
  const confidenceScore = Number.isFinite(meanConfidence) ? Math.max(0, Math.min(1, meanConfidence)) : 0;
  return (includedScore * 0.6) + (coverageScore * 0.25) + (confidenceScore * 0.15);
}

export function rankTasteMatches(
  sourceName,
  sourceVector,
  catalogItems,
  {
    limit = 4,
    minIncludedCount = 3,
    sourceMeta = null,
    metadataWeight = DEFAULT_METADATA_WEIGHT,
    diversify = false,
  } = {},
) {
  const source = normalizeTasteVector(sourceVector);
  if (!source || !Array.isArray(catalogItems) || limit <= 0) return [];
  if (Number(source.available_field_count) < 2) return [];

  const matches = [];
  for (const item of catalogItems) {
    const name = String(item?.name || "").trim();
    if (!name || name === sourceName) continue;

    const candidate = normalizeTasteVector(item?.meta?.taste_vector);
    if (!candidate || !vectorVersionsAreCompatible(source, candidate)) continue;

    const includedCount = Number(candidate.included_count);
    if (Number.isFinite(includedCount) && includedCount < minIncludedCount) continue;

    const scored = scoreTasteMatch(source, candidate, {
      sourceMeta,
      candidateMeta: item?.meta || {},
      metadataWeight,
    });
    if (!scored) continue;

    matches.push({
      item,
      name,
      profile: candidate,
      distance: scored.distance,
      evidenceScore: tasteEvidenceScore(candidate),
      tasteSimilarity: scored.tasteSimilarity,
      metadataSimilarity: scored.metadataSimilarity,
      metadataCoverage: scored.metadataCoverage,
      similarityScore: scored.similarityScore,
      metadataComparisons: scored.metadataComparisons,
      reasons: scored.reasons,
      noveltyReasons: scored.noveltyReasons,
    });
  }

  matches.sort((left, right) => (
    (right.similarityScore - left.similarityScore)
    || (left.distance - right.distance)
    || (right.evidenceScore - left.evidenceScore)
    || (right.metadataCoverage - left.metadataCoverage)
    || left.name.localeCompare(right.name)
  ));

  if (!diversify) return matches.slice(0, Math.floor(limit));

  const selected = [];
  const deferred = [];
  const seenBrands = new Set();
  const poolLimit = Math.max(Math.floor(limit) * 6, 24);
  for (const match of matches.slice(0, poolLimit)) {
    const brand = normalizeMetadataToken(match.item?.meta?.brand);
    if (brand && seenBrands.has(brand)) {
      deferred.push(match);
      continue;
    }
    selected.push(match);
    if (brand) seenBrands.add(brand);
    if (selected.length >= limit) return selected;
  }
  for (const match of deferred) {
    selected.push(match);
    if (selected.length >= limit) break;
  }
  return selected;
}
